import { useState } from "react";
import { AlertTriangle } from "lucide-react";
import { LessonCard } from "./LessonCard";
import { rangeDays, todayIso, closureFor, fmtDayHeader, STATUS } from "./calendarUtils";

const WD = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];

const DayCell = ({ d, inMonth, items, colors, overlaps, closure, selected, onSelect, onOpen }) => {
  const isToday = d === todayIso();
  return (
    <div role="button" tabIndex={0} data-testid={`cal-month-day-${d}`} onClick={() => onSelect(d)} onKeyDown={(e) => e.key === "Enter" && onSelect(d)}
      className={`min-h-[64px] lg:min-h-[120px] p-1.5 lg:p-2 border-b border-r border-border cursor-pointer transition-colors ${inMonth ? "bg-white" : "bg-muted/40 text-muted-foreground"} ${closure ? "bg-amber-50" : ""} ${selected ? "ring-2 ring-inset ring-primary" : ""}`}>
      <div className="flex items-center justify-between gap-0.5">
        <span className={`text-sm lg:text-base font-semibold w-7 h-7 flex items-center justify-center rounded-full ${isToday ? "bg-primary text-primary-foreground" : ""}`}>{Number(d.slice(8))}</span>
        {closure && <span className="text-[10px] lg:text-xs text-amber-800 font-semibold" data-testid={`cal-month-closure-${d}`} title={closure.description}><span className="lg:hidden">Ch.</span><span className="hidden lg:inline">Chiuso</span></span>}
      </div>
      {/* Telefono: pallini colorati + numero */}
      {items.length > 0 && (
        <div className="lg:hidden mt-1 flex flex-wrap items-center gap-0.5" data-testid={`cal-month-dots-${d}`}>
          {items.some((x) => overlaps.has(x.id)) && <AlertTriangle size={10} className="text-amber-700" />}
          {items.slice(0, 6).map((x) => <span key={x.id} className={`w-2 h-2 rounded-full ${x.status === "cancelled" ? "opacity-40" : ""}`} style={{ background: colors[x.course] }} />)}
          <span className="text-[11px] font-medium text-foreground ml-0.5">{items.length}</span>
        </div>
      )}
      {/* Schermi larghi: lezioni con corso e orario */}
      <div className="hidden lg:block mt-1 space-y-1">
        {items.map((x) => (
          <button key={x.id} type="button" data-testid={`cal-month-lesson-${x.id}`} onClick={(e) => { e.stopPropagation(); onOpen(x); }}
            className={`w-full text-left text-xs leading-tight rounded px-1.5 py-1 border-l-4 bg-muted/50 hover:bg-muted ${x.status === "cancelled" ? "opacity-55 line-through" : ""}`} style={{ borderLeftColor: colors[x.course] }}>
            <span className="font-semibold break-words hyphens-auto">{x.course}</span> <span className="text-muted-foreground">{x.start_time}</span>{x.status !== "regular" && <span className="ml-1 font-semibold uppercase text-[10px] text-foreground" data-testid={`cal-month-lesson-status-${x.id}`}>· {STATUS[x.status].l}</span>}{overlaps.has(x.id) && <AlertTriangle size={11} className="inline ml-1 text-amber-700" />}
          </button>
        ))}
      </div>
    </div>
  );
};

export const MonthView = ({ gridFrom, gridTo, month, lessons, colors, overlaps, closures, onOpen }) => {
  const [sel, setSel] = useState(null);
  const days = rangeDays(gridFrom, gridTo);
  const selItems = sel ? lessons.filter((x) => x.date === sel) : [];
  return (
    <div data-testid="cal-month" className="space-y-4">
      <div className="rounded-xl border-l border-t border-border overflow-hidden">
        <div className="grid grid-cols-7">
          {WD.map((w) => <div key={w} className="text-xs font-semibold uppercase tracking-wide text-muted-foreground text-center py-2 border-b border-r border-border bg-card">{w}</div>)}
          {days.map((d) => (
            <DayCell key={d} d={d} inMonth={d.slice(0, 7) === month} items={lessons.filter((x) => x.date === d)} colors={colors} overlaps={overlaps}
              closure={closureFor(d, closures)} selected={sel === d} onSelect={setSel} onOpen={onOpen} />
          ))}
        </div>
      </div>
      {sel && (
        <section data-testid="cal-month-selected" className="rounded-xl border border-border bg-card p-3 space-y-2">
          <h4 className="text-lg font-semibold capitalize">{fmtDayHeader(sel)}</h4>
          {closureFor(sel, closures) && <p className="text-sm text-amber-800">Scuola chiusa{closureFor(sel, closures).description ? ` · ${closureFor(sel, closures).description}` : ""}</p>}
          {selItems.length === 0 ? <p className="text-sm text-muted-foreground">Nessuna lezione</p>
            : selItems.map((x) => <LessonCard key={x.id} x={x} color={colors[x.course]} overlap={overlaps.has(x.id)} onOpen={onOpen} />)}
        </section>
      )}
    </div>
  );
};
